import { customClaims, redirectLoggedInTo, redirectUnauthorizedTo } from '@angular/fire/auth-guard';
import { map, pipe } from 'rxjs';
import { APP_ROUTES, FULL_ROUTES } from '../constants/routes.const';
import { UserRole } from '../models/user.model';
import { UserPlan } from '@libs/models/user.models';
import { Router, UrlTree } from '@angular/router';
import { inject } from '@angular/core';
import { UserService } from '../services/user.service';
import { AuthService } from '@libs/services/firebase/auth.service';
import { Auth } from '@angular/fire/auth';
import { TenantService } from '@libs/services/tenant.service';
import { PlanService } from '@libs/services/plan.service';
import {
  getStudentHostname,
  getTenantDomain,
  isCoreStudentDomain,
} from '../constants/domain.const';

export const authorizeAll = (): any => pipe(map(() => true));
export const redirectLogged = () => redirectLoggedInTo(FULL_ROUTES.HOME);
export const redirectUnauthorized = () => redirectUnauthorizedTo([APP_ROUTES.AUTH]);
export const authorizedOnly = (): any => pipe(customClaims, map((claims: any) => {
  if (([UserRole.STUDENT].includes(claims.role) && claims.email_verified === true)) {
    return true;
  }

  if (claims.email_verified === false) {
    return FULL_ROUTES.VERIFY_EMAIL;
  }

  return [APP_ROUTES.AUTH];
}));

export const authorizeDocuments = async (route: any): Promise<boolean | UrlTree> => {
  if (route?.params && route.params.page && route.params.page === "documents") {
    const userService = inject(UserService);
    const planService = inject(PlanService);
    const authService = inject(AuthService);
    const auth = inject(Auth);
    const router = inject(Router);

    await auth.authStateReady();

    const firebaseUser = auth.currentUser;
    if (!firebaseUser) {
      return router.createUrlTree([`/${APP_ROUTES.AUTH}`]);
    }

    const userStudent = await userService.getUserbyId(firebaseUser.uid);
    if (!userStudent || userStudent.active !== true) {
      await authService.signOut();
      return router.createUrlTree([`/${APP_ROUTES.AUTH}`])
    }

    if (userStudent.plan?.pid !== UserPlan.PLACEMENT) {
      return router.createUrlTree(['/' + FULL_ROUTES.MY_WAY_REGISTER.join("/")]);
    }

    const planUser = (await planService.load({
      where: [{
        key: 'id', condition: '==', value: userStudent.plan?.id
      }]
    }))[0];

    if (!planUser) return router.createUrlTree(['/' + FULL_ROUTES.MY_WAY_REGISTER.join("/")]);

    return true;
  }

  return true;
};

export const authorizedTenant = async (): Promise<boolean | UrlTree> => {
  const userService = inject(UserService);
  const authService = inject(AuthService);
  const tenantService = inject(TenantService);
  const auth = inject(Auth);
  const router = inject(Router);
  const hostname = getStudentHostname();

  await auth.authStateReady();

  const firebaseUser = auth.currentUser;
  if (!firebaseUser) {
    return router.createUrlTree([`/${APP_ROUTES.AUTH}`]);
  }

  const { claims } = await firebaseUser.getIdTokenResult();
  const role = claims['role'];

  if (role !== UserRole.STUDENT) {
    await authService.signOut();
    return router.createUrlTree([`/${APP_ROUTES.AUTH}`]);
  }

  if (role === UserRole.STUDENT) {
    if (claims['email_verified'] !== true) {
      return router.createUrlTree([`/${APP_ROUTES.AUTH}/${APP_ROUTES.VERIFY_EMAIL}`]);
    }
  }

  const userStudent = await userService.getUserbyId(firebaseUser.uid);
  if (!userStudent || userStudent.active !== true) {
    await authService.signOut();
    return router.createUrlTree([`/${APP_ROUTES.AUTH}`])
  }

  if (isCoreStudentDomain(hostname)) {
    return true;
  }

  const tenant = await tenantService.getTenantByDomain(getTenantDomain(hostname));
  if (!tenant) {
    await authService.signOut();
    return router.createUrlTree([`/${APP_ROUTES.AUTH}`]);
  }

  const isValidTenant = tenant.id === (userStudent?.tenant?.id ?? "");
  if (!isValidTenant) {
    await authService.signOut();
    return router.createUrlTree([`/${APP_ROUTES.AUTH}`]);
  }

  return true;
}
